import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { 
  ChevronLeft, 
  Edit3, 
  MapPin, 
  TrendingUp, 
  Award, 
  ChevronRight,
  Info,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import StudentAvatar from '../components/StudentAvatar';

const AttendanceHistory = () => {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMonth, setActiveMonth] = useState(new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }));
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const [studentRes, historyRes] = await Promise.all([
          api.get(`/students/${studentId}`),
          api.get(`/classes/history/${studentId}`)
        ]);
        setStudent(studentRes.data);
        setHistory(historyRes.data);
        
        if (historyRes.data.length > 0) {
          const latestDate = new Date(historyRes.data[0].date);
          setActiveMonth(latestDate.toLocaleString('en-US', { month: 'long', year: 'numeric' }));
          setSelectedDate(latestDate.toISOString().split('T')[0]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [studentId]);

  const stats = useMemo(() => {
    if (!history.length) return { present: 0, absent: 0, late: 0, rate: 0, currentStreak: 0, totalClasses: 0 };
    
    const present = history.filter(h => h.status === 'Present').length;
    const absent = history.filter(h => h.status === 'Absent').length;
    const late = history.filter(h => h.status === 'Late').length;
    const totalClasses = history.length;
    const rate = Math.round((present / totalClasses) * 100);

    let currentStreak = 0;
    const sortedDesc = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
    for (const h of sortedDesc) {
      if (h.status === 'Present') currentStreak++;
      else break;
    }

    return { present, absent, late, rate, currentStreak, totalClasses };
  }, [history]);

  const attendanceMap = useMemo(() => {
    const map = {};
    history.forEach(h => {
      const dateKey = new Date(h.date).toISOString().split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(h);
    });
    return map;
  }, [history]);

  const calendarGrid = useMemo(() => {
    const [monthName, yearStr] = activeMonth.split(' ');
    const monthIdx = new Date(Date.parse(monthName + " 1, 2012")).getMonth();
    const year = parseInt(yearStr);
    
    const firstDayOfMonth = new Date(year, monthIdx, 1).getDay();
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    
    const grid = [];
    let dayCount = 1;
    
    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDayOfMonth) || dayCount > daysInMonth) {
          week.push(null);
        } else {
          const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayCount).padStart(2, '0')}`;
          week.push(dateStr);
          dayCount++;
        }
      }
      grid.push(week);
      if (dayCount > daysInMonth) break;
    }
    return grid;
  }, [activeMonth]);

  const availableYears = useMemo(() => {
    const years = new Set();
    history.forEach(h => years.add(new Date(h.date).getFullYear()));
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [history]);

  const changeYear = (year) => {
    const [monthName] = activeMonth.split(' ');
    setActiveMonth(`${monthName} ${year}`);
  };

  const changeMonth = (offset) => {
    const [monthName, yearStr] = activeMonth.split(' ');
    const date = new Date(Date.parse(monthName + " 1, " + yearStr));
    date.setMonth(date.getMonth() + offset);
    setActiveMonth(date.toLocaleString('en-US', { month: 'long', year: 'numeric' }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[80vh]">
      <div className="w-16 h-16 border-4 border-indigo-50 border-t-indigo-600 rounded-full animate-spin"></div>
    </div>
  );

  if (!student) return <div className="p-8 text-center text-red-500 font-bold tracking-widest uppercase text-xs">Student Registry Error</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8 animate-in fade-in duration-700">
      
      {/* Compact Header */}
      <div className="bg-white rounded-[2.5rem] p-6 lg:p-8 border border-gray-100 shadow-xl shadow-indigo-900/5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/50 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        
        <div className="relative z-10 flex flex-col xl:flex-row justify-between items-center gap-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="relative">
              <StudentAvatar photoUrl={student.photo_url} fullName={student.full_name} className="w-24 h-24 rounded-3xl" />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 text-white rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
                <TrendingUp size={14} />
              </div>
            </div>
            <div className="text-center md:text-left space-y-2">
               <button onClick={() => window.history.back()} className="inline-flex items-center gap-1.5 text-[9px] font-black text-indigo-600 uppercase tracking-widest hover:translate-x-1 transition-transform mb-1">
                  <ChevronLeft size={14} /> Back to Registry
               </button>
               <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">{student.full_name}</h1>
               <div className="flex items-center justify-center md:justify-start gap-3">
                  <span className="px-3 py-1 bg-gray-50 rounded-lg text-[10px] font-bold text-gray-500 flex items-center gap-1.5 border border-gray-100">
                    <MapPin size={12} className="text-indigo-400" /> {student.abroad_study_country || 'Japan'}
                  </span>
                  <span className="px-3 py-1 bg-indigo-50 rounded-lg text-[10px] font-black text-indigo-600 border border-indigo-100">ID: #{student.id}</span>
               </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 w-full xl:w-auto">
            {[
              { label: 'Rate', val: `${stats.rate}%`, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: Award },
              { label: 'Present', val: stats.present, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle2 },
              { label: 'Absent', val: stats.absent, color: 'text-rose-600', bg: 'bg-rose-50', icon: XCircle },
              { label: 'Late', val: stats.late, color: 'text-amber-600', bg: 'bg-amber-50', icon: AlertCircle },
              { label: 'Classes', val: stats.totalClasses, color: 'text-blue-600', bg: 'bg-blue-50', icon: Layers },
              { label: 'Streak', val: stats.currentStreak, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: TrendingUp },
            ].map((stat, i) => (
              <div key={i} className={`${stat.bg} p-4 rounded-2xl border border-white flex flex-col items-center justify-center text-center shadow-sm hover:scale-105 transition-all duration-300`}>
                <stat.icon size={14} className={`${stat.color} mb-1 opacity-70`} />
                <h4 className={`text-lg font-black ${stat.color} leading-none mb-1`}>{stat.val}</h4>
                <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Compact Calendar */}
        <div className="lg:col-span-8">
          <div className="bg-white p-6 lg:p-10 rounded-[3rem] border border-gray-100 shadow-xl shadow-indigo-900/5 overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="flex gap-1.5">
                  <button onClick={() => changeMonth(-1)} className="p-3 bg-gray-50 hover:bg-indigo-600 text-gray-400 hover:text-white rounded-xl transition-all shadow-sm"><ChevronLeft size={18} /></button>
                  <h2 className="text-3xl font-black text-gray-900 tracking-tighter min-w-[160px] text-center">{activeMonth.split(' ')[0]}</h2>
                  <button onClick={() => changeMonth(1)} className="p-3 bg-gray-50 hover:bg-indigo-600 text-gray-400 hover:text-white rounded-xl transition-all shadow-sm"><ChevronRight size={18} /></button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 w-full sm:w-auto">
                <select 
                  value={activeMonth.split(' ')[1]} 
                  onChange={(e) => changeYear(e.target.value)}
                  className="bg-gray-50 border border-gray-100 px-6 py-2.5 rounded-xl text-base font-black text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer appearance-none min-w-[120px] text-center shadow-sm"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                
                <div className="flex flex-wrap gap-2">
                  {['Present', 'Absent', 'Late'].map(s => (
                    <div key={s} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100 shadow-sm">
                      <div className={`w-2.5 h-2.5 rounded-full ${s === 'Present' ? 'bg-emerald-500' : s === 'Absent' ? 'bg-rose-500' : 'bg-amber-500'}`}></div>
                      <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-3 sm:gap-4">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
                <div key={d} className="text-center text-[9px] font-black text-gray-300 py-2">{d}</div>
              ))}
              {calendarGrid.flat().map((date, i) => {
                const dayNum = date ? parseInt(date.split('-')[2]) : null;
                const records = date ? (attendanceMap[date] || []) : [];
                const mainStatus = records.length > 0 ? records[0].status : null;
                const isSelected = selectedDate === date;
                const isToday = new Date().toISOString().split('T')[0] === date;

                return (
                  <div key={i} className="aspect-square relative">
                    {date && (
                      <button
                        onClick={() => setSelectedDate(date)}
                        className={`w-full h-full rounded-2xl flex flex-col items-center justify-center transition-all duration-300 border-2 ${
                          isSelected ? 'border-indigo-600 shadow-xl scale-105 z-10' : 'border-transparent'
                        } ${
                          mainStatus === 'Present' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' :
                          mainStatus === 'Absent' ? 'bg-rose-500 text-white shadow-lg shadow-rose-200' :
                          mainStatus === 'Late' ? 'bg-amber-500 text-white shadow-lg shadow-amber-200' :
                          'bg-gray-50 text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        <span className={`text-sm sm:text-base font-black ${isSelected && !mainStatus ? 'text-indigo-600' : ''}`}>{dayNum}</span>
                        {records.length > 0 && <div className="absolute bottom-1.5 flex gap-0.5">{records.map((_, idx) => <div key={idx} className="w-1 h-1 rounded-full bg-current opacity-60"></div>)}</div>}
                        {isToday && <div className={`absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full ${mainStatus ? 'bg-white shadow-sm' : 'bg-indigo-600'}`}></div>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Compact Details */}
        <div className="lg:col-span-4 h-full">
          <div className="sticky top-6 space-y-4">
            <div className="bg-indigo-900 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-indigo-900/20 overflow-hidden min-h-[400px]">
              <div className="relative z-10 space-y-8">
                <div className="space-y-1">
                  <p className="text-[9px] font-black uppercase tracking-[0.3em] opacity-40">Session Logs</p>
                  <h3 className="text-xl font-black">{new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</h3>
                  <div className="w-10 h-0.5 bg-indigo-400/30 mt-3"></div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={selectedDate} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                    {(!attendanceMap[selectedDate] || attendanceMap[selectedDate].length === 0) ? (
                      <div className="py-12 text-center opacity-30 flex flex-col items-center">
                        <Info size={40} className="mb-2" />
                        <p className="text-[10px] font-black uppercase tracking-widest">No Logs Found</p>
                      </div>
                    ) : (
                      attendanceMap[selectedDate].map((record, idx) => (
                        <div key={idx} className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-sm space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-0.5">
                              <h4 className="font-black text-sm">{record.class_name}</h4>
                              <p className="text-[9px] font-black uppercase tracking-widest opacity-60">{record.time_slot}</p>
                            </div>
                            <Link to={`/attendance/${record.class_id}?date=${selectedDate}`} className="p-2.5 bg-white/10 hover:bg-white text-white hover:text-indigo-900 rounded-xl transition-all"><Edit3 size={14} /></Link>
                          </div>
                          <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                            <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${record.status === 'Present' ? 'bg-emerald-500' : record.status === 'Absent' ? 'bg-rose-500' : 'bg-amber-500'}`}>{record.status}</span>
                            <span className="text-[9px] font-black opacity-40 uppercase tracking-tighter italic">By {record.creator_name || 'Staff'}</span>
                          </div>
                          {record.remarks && <p className="text-[10px] font-medium opacity-70 bg-black/20 p-3 rounded-xl border border-white/5 italic">"{record.remarks}"</p>}
                        </div>
                      ))
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceHistory;
